/*
 * ET - PDP Why Finex (sections/et-pdp-why-finex.liquid)
 * <et-pdp-why-finex-slider>: the cards are a static row from 990px and a Swiper slider below 990px
 * (slide width from CSS, 16px gap, Scrollbar module on the shared .et-slider-progress bar, optional autoplay from
 * data-autoplay / data-autoplay-delay, off for prefers-reduced-motion and paused while a card is selected in the editor).
 * Autoplay stops for good once the visitor swipes, drags the progress bar or moves keyboard focus into the slider.
 * Swiper is created/destroyed on the breakpoint change and destroyed on disconnect, so theme editor re-renders
 * (shopify:section:load / unload) never leave a duplicate instance. Blocks selected in the editor slide into view.
 */
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('et-pdp-why-finex-slider')) return;

  const SLIDER_QUERY = '(max-width: 989px)';
  const PROGRESS_THUMB_SIZE = 39;
  const SLIDE_SELECTOR = '[data-et-why-finex-slide]';

  class EtPdpWhyFinexSlider extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.loading = false;
      this.pendingIndex = null;
      this.blockSelected = false;
      this.visitorStopped = false;
      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.handleFocusIn = this.handleFocusIn.bind(this);
    }

    connectedCallback() {
      this.addEventListener('focusin', this.handleFocusIn);
      this.media = window.matchMedia(SLIDER_QUERY);
      if (typeof this.media.addEventListener === 'function') {
        this.media.addEventListener('change', this.handleMediaChange);
      } else if (typeof this.media.addListener === 'function') {
        this.media.addListener(this.handleMediaChange);
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.handleMediaChange, { once: true });
      } else {
        this.handleMediaChange();
      }
    }

    disconnectedCallback() {
      this.removeEventListener('focusin', this.handleFocusIn);
      if (this.media) {
        if (typeof this.media.removeEventListener === 'function') {
          this.media.removeEventListener('change', this.handleMediaChange);
        } else if (typeof this.media.removeListener === 'function') {
          this.media.removeListener(this.handleMediaChange);
        }
      }
      document.removeEventListener('DOMContentLoaded', this.handleMediaChange);
      this.destroy();
    }

    handleFocusIn() {
      this.visitorStopped = true;
      this.pauseAutoplay();
    }

    handleMediaChange() {
      if (!this.isConnected) return;
      if (this.media && this.media.matches) this.init();
      else this.destroy();
    }

    init() {
      const swiperEl = this.querySelector('[data-et-why-finex-swiper]');
      if (!swiperEl || this.swiper || this.loading || typeof window.etLoadSwiper !== 'function') return;
      if (!this.media || !this.media.matches) return;

      this.loading = true;
      window.etLoadSwiper().then((Swiper) => {
        this.loading = false;
        if (!Swiper || !this.isConnected || this.swiper || !this.media.matches) return;

        const progressEl = this.querySelector('[data-et-why-finex-progress]');
        const autoplay = this.autoplayEnabled();
        this.swiper = new Swiper(swiperEl, {
          slidesPerView: 'auto',
          spaceBetween: 16,
          speed: 400,
          watchOverflow: true,
          grabCursor: true,
          rewind: autoplay,
          autoplay: autoplay
            ? {
                delay: parseInt(this.dataset.autoplayDelay, 10) || 5000,
                disableOnInteraction: true,
                pauseOnMouseEnter: true
              }
            : false,
          scrollbar: progressEl ? { el: progressEl, draggable: true, dragSize: PROGRESS_THUMB_SIZE } : false,
          a11y: { enabled: true }
        });

        if (this.pendingIndex !== null) {
          this.swiper.slideTo(this.pendingIndex, 0);
          this.pendingIndex = null;
        }
        if (this.blockSelected || this.visitorStopped) this.pauseAutoplay();
      });
    }

    autoplayEnabled() {
      if (this.dataset.autoplay !== 'true') return false;
      return !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }

    pauseAutoplay() {
      if (this.swiper && this.swiper.autoplay && this.swiper.autoplay.running) this.swiper.autoplay.stop();
    }

    resumeAutoplay() {
      this.blockSelected = false;
      if (this.visitorStopped) return;
      if (this.swiper && this.swiper.autoplay && this.autoplayEnabled() && !this.swiper.autoplay.running) {
        this.swiper.autoplay.start();
      }
    }

    destroy() {
      if (!this.swiper) return;
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    selectSlide(slide) {
      const slides = Array.from(this.querySelectorAll(SLIDE_SELECTOR));
      const index = slides.indexOf(slide);
      if (index < 0) return;

      this.blockSelected = true;
      this.pauseAutoplay();
      if (this.swiper) this.swiper.slideTo(index, 0);
      else if (this.media && this.media.matches) this.pendingIndex = index;
    }
  }

  window.customElements.define('et-pdp-why-finex-slider', EtPdpWhyFinexSlider);

  const slidersIn = (scope) =>
    scope instanceof Element || scope === document ? Array.from(scope.querySelectorAll('et-pdp-why-finex-slider')) : [];

  document.addEventListener('shopify:section:load', (event) => slidersIn(event.target).forEach((el) => el.init()));
  document.addEventListener('shopify:section:unload', (event) => slidersIn(event.target).forEach((el) => el.destroy()));

  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target instanceof Element ? event.target.closest(SLIDE_SELECTOR) : null;
    const slider = slide && slide.closest('et-pdp-why-finex-slider');
    if (slider) slider.selectSlide(slide);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const slider = event.target instanceof Element ? event.target.closest('et-pdp-why-finex-slider') : null;
    if (slider) slider.resumeAutoplay();
  });
})();
